import type {
  GetMessagesResponse,
  GetTicketsResponse,
  MarkAsReadResponse,
  Message,
  SendMessageResponse,
  Ticket,
  UserProvidedTraits,
} from 'posthog-js';
import type { SupportIdentity, SupportMessage, SupportTicket } from './types';

export type PostHogSupportErrorCode =
  | 'attachmentsUnsupported'
  | 'identityChanged'
  | 'providerUnavailable'
  | 'ticketAddressMismatch';

export class PostHogSupportError extends Error {
  constructor(readonly code: PostHogSupportErrorCode) {
    super(code);
    this.name = 'PostHogSupportError';
  }
}

interface ConversationsApi {
  hide(): void;
  isAvailable(): boolean;
  getUnavailableReason(): string | null;
  getCurrentTicketId(): string | null;
  getTickets(options?: {
    limit?: number;
    offset?: number;
    status?: string;
  }): Promise<GetTicketsResponse | null>;
  getMessages(
    ticketId?: string,
    after?: string
  ): Promise<GetMessagesResponse | null>;
  markAsRead(ticketId?: string): Promise<MarkAsReadResponse | null>;
  sendMessage(
    message: string,
    userTraits?: UserProvidedTraits,
    newTicket?: boolean
  ): Promise<SendMessageResponse | null>;
}

export interface PostHogSupportSdk {
  conversations: ConversationsApi;
  setIdentity(distinctId: string, hash: string): void;
  clearIdentity(): void;
}

export interface SupportTicketPage {
  tickets: SupportTicket[];
  hasMore: boolean;
}

export interface SelectedSupportTicket {
  id: string;
  status: string;
  unreadCount: number;
  messages: SupportMessage[];
  hasMoreMessages: boolean;
  latestMessageCreatedAt: string | null;
}

const TICKET_PAGE_SIZE = 20;
const TICKET_TITLE_ID_LENGTH = 8;

function firstNonEmptyLine(value: string | undefined): string | undefined {
  return value
    ?.split(/\r?\n/)
    .map((line) => line.trim())
    .find(Boolean);
}

export function getSupportTicketFallbackTitle(ticketId: string): string {
  return `#${ticketId.slice(0, TICKET_TITLE_ID_LENGTH)}`;
}

function requiredResponse<T>(value: T | null): T {
  if (value === null) throw new PostHogSupportError('providerUnavailable');
  return value;
}

export function deriveSupportTicketTitle(
  ticketId: string,
  messages: readonly SupportMessage[],
  existingTitle?: string
): string {
  const fallbackTitle = getSupportTicketFallbackTitle(ticketId);
  const stableExistingTitle = firstNonEmptyLine(existingTitle);
  if (stableExistingTitle && stableExistingTitle !== fallbackTitle) {
    return stableExistingTitle;
  }

  const firstCustomerMessage = messages
    .filter(
      (message) => message.author === 'customer' && message.content.trim()
    )
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt))[0];
  return firstNonEmptyLine(firstCustomerMessage?.content) || fallbackTitle;
}

export function mapSupportTicket(ticket: Ticket): SupportTicket {
  const messages = (ticket.messages ?? [])
    .filter((message) => !message.is_private)
    .map(mapSupportMessage);
  return {
    id: ticket.id,
    status: ticket.status,
    title: deriveSupportTicketTitle(ticket.id, messages),
    updatedAt: ticket.last_message_at ?? ticket.created_at,
    unreadCount: ticket.unread_count ?? 0,
  };
}

export function mapSupportMessage(message: Message): SupportMessage {
  return {
    id: message.id,
    content: message.content,
    author:
      message.author_type === 'customer'
        ? 'customer'
        : message.author_type === 'AI'
          ? 'ai'
          : 'support',
    createdAt: message.created_at,
  };
}

export class PostHogSupportClient {
  private identityEpoch = 0;
  private ticketQueue: Promise<void> = Promise.resolve();
  private identity: SupportIdentity | null = null;
  private identityKnown = false;

  constructor(private readonly sdk: PostHogSupportSdk) {}

  setIdentity(identity: SupportIdentity): void {
    if (
      this.identity?.distinctId === identity.distinctId &&
      this.identity.identityHash === identity.identityHash
    ) {
      this.sdk.conversations.hide();
      return;
    }
    this.identityEpoch += 1;
    this.identity = identity;
    this.identityKnown = true;
    this.sdk.setIdentity(identity.distinctId, identity.identityHash);
    this.sdk.conversations.hide();
  }

  clearIdentity(): void {
    if (this.identityKnown && this.identity === null) {
      this.sdk.conversations.hide();
      return;
    }
    this.identityEpoch += 1;
    this.identity = null;
    this.identityKnown = true;
    this.sdk.conversations.hide();
    this.sdk.clearIdentity();
  }

  clearIdentityIfDifferent(distinctId: string): void {
    if (this.identity?.distinctId !== distinctId) this.clearIdentity();
  }

  hideWidget(): void {
    this.sdk.conversations.hide();
  }

  isAvailable(): boolean {
    return this.sdk.conversations.isAvailable();
  }

  getUnavailableReason(): string | null {
    return this.sdk.conversations.getUnavailableReason();
  }

  async listTickets(offset = 0): Promise<SupportTicketPage> {
    const epoch = this.identityEpoch;
    const response = requiredResponse(
      await this.sdk.conversations.getTickets({
        limit: TICKET_PAGE_SIZE,
        offset,
      })
    );
    this.assertEpoch(epoch);
    return {
      tickets: response.results.map(mapSupportTicket),
      hasMore: offset + response.results.length < response.count,
    };
  }

  selectTicket(ticketId: string): Promise<SelectedSupportTicket> {
    return this.readTicket(ticketId, undefined, true);
  }

  refreshTicket(
    ticketId: string,
    after?: string
  ): Promise<SelectedSupportTicket> {
    return this.readTicket(ticketId, after, false);
  }

  private readTicket(
    ticketId: string,
    after: string | undefined,
    alwaysMarkAsRead: boolean
  ): Promise<SelectedSupportTicket> {
    const epoch = this.identityEpoch;
    return this.serializeTicketOperation(async () => {
      this.assertEpoch(epoch);
      const firstResponse = requiredResponse(
        await this.sdk.conversations.getMessages(ticketId, after)
      );
      this.assertEpoch(epoch);
      if (firstResponse.ticket_id !== ticketId)
        throw new PostHogSupportError('ticketAddressMismatch');

      const messages = [...firstResponse.messages];
      let response = firstResponse;
      let unreadCount = firstResponse.unread_count;
      let pageCount = 1;
      while (
        response.has_more &&
        response.messages.length > 0 &&
        pageCount < 20
      ) {
        const after = response.messages.at(-1)?.created_at;
        if (!after) break;
        response = requiredResponse(
          await this.sdk.conversations.getMessages(ticketId, after)
        );
        this.assertEpoch(epoch);
        if (response.ticket_id !== ticketId)
          throw new PostHogSupportError('ticketAddressMismatch');
        messages.push(...response.messages);
        unreadCount = Math.max(unreadCount, response.unread_count);
        pageCount += 1;
      }

      if (alwaysMarkAsRead || unreadCount > 0) {
        const read = requiredResponse(
          await this.sdk.conversations.markAsRead(ticketId)
        );
        this.assertEpoch(epoch);
        if (!read.success) throw new PostHogSupportError('providerUnavailable');
        unreadCount = read.unread_count;
      }

      const latestMessageCreatedAt = messages.reduce<string | null>(
        (latest, message) =>
          !latest || message.created_at > latest ? message.created_at : latest,
        null
      );

      return {
        id: response.ticket_id,
        status: response.ticket_status,
        unreadCount,
        messages: messages
          .filter((message) => !message.is_private)
          .map(mapSupportMessage),
        hasMoreMessages: response.has_more,
        latestMessageCreatedAt,
      };
    });
  }

  sendNew(message: string): Promise<SendMessageResponse> {
    const epoch = this.identityEpoch;
    return this.serializeTicketOperation(async () => {
      this.assertEpoch(epoch);
      const response = requiredResponse(
        await this.sdk.conversations.sendMessage(message, undefined, true)
      );
      this.assertEpoch(epoch);
      return response;
    });
  }

  replyToTicket(
    ticketId: string,
    message: string
  ): Promise<SendMessageResponse> {
    const epoch = this.identityEpoch;
    return this.serializeTicketOperation(async () => {
      this.assertEpoch(epoch);
      const selected = requiredResponse(
        await this.sdk.conversations.getMessages(ticketId)
      );
      this.assertEpoch(epoch);
      if (
        selected.ticket_id !== ticketId ||
        this.sdk.conversations.getCurrentTicketId() !== ticketId
      ) {
        throw new PostHogSupportError('ticketAddressMismatch');
      }

      const response = requiredResponse(
        await this.sdk.conversations.sendMessage(message, undefined, false)
      );
      this.assertEpoch(epoch);
      if (response.ticket_id !== ticketId)
        throw new PostHogSupportError('ticketAddressMismatch');
      return response;
    });
  }

  private assertEpoch(expected: number): void {
    if (this.identityEpoch !== expected)
      throw new PostHogSupportError('identityChanged');
  }

  private serializeTicketOperation<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.ticketQueue.then(operation, operation);
    this.ticketQueue = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  }
}

const clientsBySdk = new WeakMap<object, PostHogSupportClient>();

export function getPostHogSupportClient(
  sdk: PostHogSupportSdk
): PostHogSupportClient {
  const existing = clientsBySdk.get(sdk);
  if (existing) return existing;
  const client = new PostHogSupportClient(sdk);
  clientsBySdk.set(sdk, client);
  return client;
}

export function clearPostHogSupportIdentity(sdk: PostHogSupportSdk): void {
  clientsBySdk.get(sdk)?.clearIdentity();
}

export async function waitForPostHogSupport(
  client: PostHogSupportClient,
  options: {
    signal: AbortSignal;
    timeoutMs?: number;
    pollIntervalMs?: number;
  }
): Promise<boolean> {
  const timeoutMs = options.timeoutMs ?? 5_000;
  const pollIntervalMs = options.pollIntervalMs ?? 100;
  const deadline = Date.now() + timeoutMs;
  client.hideWidget();

  while (!options.signal.aborted && Date.now() < deadline) {
    if (client.isAvailable()) {
      client.hideWidget();
      return true;
    }
    await new Promise<void>((resolve) => {
      const timeout = window.setTimeout(resolve, pollIntervalMs);
      options.signal.addEventListener(
        'abort',
        () => {
          window.clearTimeout(timeout);
          resolve();
        },
        { once: true }
      );
    });
  }

  const available = !options.signal.aborted && client.isAvailable();
  if (available) client.hideWidget();
  return available;
}
